<template>
  <main-template>
    <v-row align="center" class="mb-1">
      <v-col>
        <h1>
          <v-btn color="primary" x-small text @click="$router.go(-1)">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          Gestão do Banco de Horas
        </h1>
      </v-col>
    </v-row>

    <v-tabs v-model="tab" color="primary" class="mb-6">
      <v-tab class="font-weight-bold">
        <v-icon left>mdi-clock-alert-outline</v-icon>
        Aprovações Pendentes
        <v-chip
          v-if="pendenciasTotal > 0"
          small
          color="orange darken-2"
          class="ml-2 white--text font-weight-bold"
        >
          {{ pendenciasTotal }}
        </v-chip>
      </v-tab>
      <v-tab class="font-weight-bold">
        <v-icon left>mdi-account-group-outline</v-icon>
        Saldos dos Colaboradores
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" class="transparent">
      <!-- TAB 1: PENDÊNCIAS DE APROVAÇÃO -->
      <v-tab-item>
        <v-card outlined rounded="xl">
          <v-card-title class="px-6 pt-6 pb-2 d-flex justify-space-between align-center flex-wrap">
            <div>
              <span class="text-h6 font-weight-bold">Horas Extras Aguardando Aprovação</span>
            </div>
            <v-btn icon color="primary" @click="carregarPendencias(1)" :loading="loadingPendencias">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="px-6 pb-6">
            <v-row class="mb-4">
              <v-col cols="12" sm="6" md="4">
                <e-label>Buscar Colaborador</e-label>
                <v-text-field
                  v-model="filtrosPendencias.search"
                  placeholder="Nome do colaborador..."
                  dense
                  solo
                  hide-details
                  clearable
                  append-icon="mdi-magnify"
                  @keyup.enter="carregarPendencias(1)"
                  @click:clear="carregarPendencias(1)"
                />
              </v-col>
              <v-col cols="12" sm="3" md="3">
                <e-label>Data Início</e-label>
                <e-date-customize
                  v-model="filtrosPendencias.data_inicio"
                  @change="carregarPendencias(1)"
                />
              </v-col>
              <v-col cols="12" sm="3" md="3">
                <e-label>Data Fim</e-label>
                <e-date-customize
                  v-model="filtrosPendencias.data_fim"
                  @change="carregarPendencias(1)"
                />
              </v-col>
            </v-row>

            <v-data-table
              :headers="headersPendencias"
              :items="pendencias.data || []"
              :loading="loadingPendencias"
              hide-default-footer
              class="elevation-0"
            >
              <template v-slot:item.user="{ item }">
                <div class="font-weight-bold">
                  {{ item.user ? item.user.name : "-" }}
                </div>
                <div
                  class="text-caption grey--text"
                  v-if="item.user && item.user.recursos_humanos && item.user.recursos_humanos.lider"
                >
                  Líder: {{ item.user.recursos_humanos.lider.name }}
                </div>
              </template>

              <template v-slot:item.data="{ item }">
                <span class="font-weight-bold grey--text text--darken-3">
                  {{ formatarData(item.data) }}
                </span>
              </template>

              <template v-slot:item.minutos_previstos="{ item }">
                {{ formatarMinutos(item.minutos_previstos) }}
              </template>

              <template v-slot:item.minutos_realizados="{ item }">
                {{ formatarMinutos(item.minutos_realizados) }}
              </template>

              <template v-slot:item.minutos_extras="{ item }">
                <v-chip
                  color="orange lighten-5"
                  text-color="orange darken-3"
                  class="font-weight-bold"
                >
                  +{{ formatarMinutos(item.minutos_extras) }}
                </v-chip>
              </template>

              <template v-slot:item.acoes="{ item }">
                <div class="d-flex ga-2">
                  <v-btn
                    color="success"
                    small
                    class="rounded-lg font-weight-bold"
                    @click="abrirModalAprovar(item)"
                  >
                    <v-icon left small>mdi-check-circle-outline</v-icon> Aprovar
                  </v-btn>
                  <v-btn
                    color="error"
                    outlined
                    small
                    class="rounded-lg font-weight-bold ml-2"
                    @click="abrirModalReprovar(item)"
                  >
                    <v-icon left small>mdi-close-circle-outline</v-icon> Reprovar
                  </v-btn>
                  <v-btn
                    color="primary"
                    outlined
                    small
                    class="rounded-lg font-weight-bold ml-2"
                    @click="verPontosDoDia(item.user_id || (item.user && item.user.id), item.data)"
                  >
                    <v-icon left small>mdi-clock-outline</v-icon> Ver registro
                  </v-btn>
                </div>
              </template>

              <template v-slot:no-data>
                <div class="pa-6 text-center grey--text">
                  <v-icon size="48" color="green lighten-2">mdi-check-all</v-icon>
                  <div class="mt-2 text-subtitle-1">
                    Nenhuma solicitação de horas extras pendente de aprovação.
                  </div>
                </div>
              </template>
            </v-data-table>

            <v-pagination
              v-if="pendencias.last_page > 1"
              v-model="pendencias.current_page"
              :length="pendencias.last_page"
              @input="carregarPendencias"
              class="mt-4"
            />
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- TAB 2: SALDOS DOS COLABORADORES -->
      <v-tab-item>
        <v-card outlined rounded="xl">
          <v-card-title class="px-6 pt-6 pb-2 d-flex justify-space-between align-center flex-wrap">
            <div>
              <span class="text-h6 font-weight-bold">Saldos de Banco de Horas da Equipe</span>
            </div>
            <v-btn icon color="primary" @click="carregarSaldos(1)" :loading="loadingSaldos">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="px-6 pb-6">
            <v-row class="mb-4">
              <v-col cols="12" sm="6" md="4">
                <e-label>Buscar Colaborador</e-label>
                <v-text-field
                  v-model="filtrosSaldos.search"
                  placeholder="Nome ou e-mail..."
                  dense
                  solo
                  hide-details
                  clearable
                  append-icon="mdi-magnify"
                  @keyup.enter="carregarSaldos(1)"
                  @click:clear="carregarSaldos(1)"
                />
              </v-col>
            </v-row>

            <v-data-table
              :headers="headersSaldos"
              :items="saldos.data || []"
              :loading="loadingSaldos"
              hide-default-footer
              class="elevation-0"
            >
              <template v-slot:item.name="{ item }">
                <div class="font-weight-bold">{{ item.name }}</div>
                <div class="text-caption grey--text">{{ item.email }}</div>
              </template>

              <template v-slot:item.lider="{ item }">
                <span v-if="item.lider" class="grey--text text--darken-3 font-weight-medium">
                  {{ item.lider }}
                </span>
                <span v-else class="grey--text font-italic">Sem líder definido</span>
              </template>

              <template v-slot:item.saldo_formatado="{ item }">
                <span
                  :class="
                    item.saldo_minutos >= 0
                      ? 'green--text text--darken-2 font-weight-black text-subtitle-1'
                      : 'red--text font-weight-black text-subtitle-1'
                  "
                >
                  {{ item.saldo_formatado }}
                </span>
              </template>

              <template v-slot:item.horas_positivas_fmt="{ item }">
                <span class="green--text text--darken-2 font-weight-bold">
                  {{ item.horas_positivas_fmt }}
                </span>
              </template>

              <template v-slot:item.horas_negativas_fmt="{ item }">
                <span class="red--text font-weight-bold">
                  {{ item.horas_negativas_fmt }}
                </span>
              </template>

              <template v-slot:item.minutos_pendentes_fmt="{ item }">
                <v-chip
                  v-if="item.minutos_pendentes_fmt !== '00:00'"
                  small
                  color="orange lighten-5"
                  text-color="orange darken-3"
                  class="font-weight-bold"
                >
                  {{ item.minutos_pendentes_fmt }}
                </v-chip>
                <span v-else class="grey--text">00:00</span>
              </template>

              <template v-slot:item.acoes="{ item }">
                <div class="d-flex ga-2">
                  <v-btn
                    color="primary"
                    outlined
                    small
                    class="rounded-lg font-weight-bold"
                    @click="verHistoricoColaborador(item)"
                  >
                    <v-icon left small>mdi-eye-outline</v-icon> Histórico
                  </v-btn>
                </div>
              </template>

              <template v-slot:no-data>
                <div class="pa-6 text-center grey--text">
                  <v-icon size="48" color="grey lighten-1">mdi-account-off-outline</v-icon>
                  <div class="mt-2">Nenhum colaborador encontrado.</div>
                </div>
              </template>
            </v-data-table>

            <v-pagination
              v-if="saldos.last_page > 1"
              v-model="saldos.current_page"
              :length="saldos.last_page"
              @input="carregarSaldos"
              class="mt-4"
            />
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- MODAL APROVAR -->
    <v-dialog v-model="modalAprovar.show" max-width="500" persistent>
      <v-card rounded="xl">
        <v-card-title class="px-6 pt-6 pb-2 font-weight-bold">
          <v-icon left color="success">mdi-check-circle</v-icon>
          Aprovar Horas Extras
        </v-card-title>
        <v-card-text class="px-6 py-2" v-if="modalAprovar.item">
          <p>
            Deseja aprovar
            <strong>{{ formatarMinutos(modalAprovar.item.minutos_extras) }}</strong> de horas extras
            para o colaborador
            <strong>{{ modalAprovar.item.user ? modalAprovar.item.user.name : "" }}</strong>
            referente ao dia {{ formatarData(modalAprovar.item.data) }}?
          </p>
          <e-label>Observação (Opcional)</e-label>
          <v-textarea
            v-model="modalAprovar.observacoes"
            placeholder="Digite uma observação se desejar..."
            rows="3"
            dense
            solo
          />
        </v-card-text>
        <v-card-actions class="px-6 pb-6 pt-0 d-flex justify-end ga-2">
          <v-btn text @click="modalAprovar.show = false" :disabled="modalAprovar.loading"
            >Cancelar</v-btn
          >
          <v-btn
            color="success"
            class="rounded-lg font-weight-bold"
            @click="confirmarAprovacao"
            :loading="modalAprovar.loading"
          >
            Confirmar Aprovação
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- MODAL REPROVAR -->
    <v-dialog v-model="modalReprovar.show" max-width="500" persistent>
      <v-card rounded="xl">
        <v-card-title class="px-6 pt-6 pb-2 font-weight-bold error--text">
          <v-icon left color="error">mdi-close-circle</v-icon>
          Reprovar Horas Extras
        </v-card-title>
        <v-card-text class="px-6 py-2" v-if="modalReprovar.item">
          <p>
            Deseja reprovar
            <strong>{{ formatarMinutos(modalReprovar.item.minutos_extras) }}</strong> de horas
            extras do colaborador
            <strong>{{ modalReprovar.item.user ? modalReprovar.item.user.name : "" }}</strong>
            referente ao dia {{ formatarData(modalReprovar.item.data) }}?
          </p>
          <e-label>Motivo da Reprovação</e-label>
          <v-textarea
            v-model="modalReprovar.motivo"
            placeholder="Informe o motivo da reprovação..."
            rows="3"
            dense
            solo
          />
        </v-card-text>
        <v-card-actions class="px-6 pb-6 pt-0 d-flex justify-end ga-2">
          <v-btn text @click="modalReprovar.show = false" :disabled="modalReprovar.loading"
            >Cancelar</v-btn
          >
          <v-btn
            color="error"
            class="rounded-lg font-weight-bold"
            @click="confirmarReprovacao"
            :loading="modalReprovar.loading"
          >
            Confirmar Reprovação
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- MODAL HISTÓRICO DO COLABORADOR -->
    <v-dialog v-model="modalHistorico.show" max-width="850">
      <v-card rounded="xl">
        <v-card-title class="px-6 pt-6 pb-2 d-flex justify-space-between align-center">
          <div class="font-weight-bold">
            Histórico: {{ modalHistorico.user ? modalHistorico.user.name : "" }}
          </div>
          <v-btn icon @click="modalHistorico.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="px-6 pb-6">
          <v-data-table
            :headers="headersHistorico"
            :items="modalHistorico.dados.data || []"
            :loading="modalHistorico.loading"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.data="{ item }">
              {{ formatarData(item.data) }}
            </template>
            <template v-slot:item.minutos_previstos="{ item }">
              {{ formatarMinutos(item.minutos_previstos) }}
            </template>
            <template v-slot:item.minutos_realizados="{ item }">
              {{ formatarMinutos(item.minutos_realizados) }}
            </template>
            <template v-slot:item.minutos_extras="{ item }">
              <span v-if="item.tipo === 'debito'" class="red--text font-weight-bold">
                -{{ formatarMinutos(item.minutos_debito) }}
              </span>
              <span v-else class="green--text text--darken-2 font-weight-bold">
                +{{ formatarMinutos(item.minutos_extras) }}
              </span>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip
                small
                :color="getStatusColor(item.status)"
                class="white--text font-weight-bold"
              >
                {{ getStatusText(item.status) }}
              </v-chip>
            </template>
            <template v-slot:item.aprovador="{ item }">
              {{ item.aprovador ? item.aprovador.name : "-" }}
            </template>
            <template v-slot:item.acoes="{ item }">
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="verPontosDoDia(modalHistorico.user.id, item.data)"
                  >
                    <v-icon small>mdi-clock-outline</v-icon>
                  </v-btn>
                </template>
                <span>Ver registro do dia</span>
              </v-tooltip>
            </template>
          </v-data-table>
          <v-pagination
            v-if="modalHistorico.dados.last_page > 1"
            v-model="modalHistorico.dados.current_page"
            :length="modalHistorico.dados.last_page"
            @input="(p) => carregarHistoricoModal(modalHistorico.user.id, p)"
            class="mt-4"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
export default {
  name: "GerenciarBancoHorasPage",
  data() {
    return {
      tab: 0,
      loadingPendencias: false,
      loadingSaldos: false,
      pendencias: { data: [], current_page: 1, last_page: 1, total: 0 },
      saldos: { data: [], current_page: 1, last_page: 1 },
      filtrosPendencias: {
        search: "",
        data_inicio: null,
        data_fim: null,
      },
      filtrosSaldos: {
        search: "",
      },
      headersPendencias: [
        { text: "Colaborador", value: "user", sortable: false },
        { text: "Data", value: "data", sortable: false },
        { text: "Previsto", value: "minutos_previstos", sortable: false },
        { text: "Realizado", value: "minutos_realizados", sortable: false },
        { text: "Horas Extras", value: "minutos_extras", sortable: false },
        { text: "Ações", value: "acoes", sortable: false },
      ],
      headersSaldos: [
        { text: "Colaborador", value: "name", sortable: false },
        { text: "Líder Imediato", value: "lider", sortable: false },
        { text: "Saldo Atual", value: "saldo_formatado", sortable: false },
        { text: "Positivas", value: "horas_positivas_fmt", sortable: false },
        { text: "Negativas", value: "horas_negativas_fmt", sortable: false },
        { text: "Pendentes", value: "minutos_pendentes_fmt", sortable: false },
        { text: "Ações", value: "acoes", sortable: false },
      ],
      headersHistorico: [
        { text: "Data", value: "data", sortable: false },
        { text: "Previsto", value: "minutos_previstos", sortable: false },
        { text: "Realizado", value: "minutos_realizados", sortable: false },
        { text: "Horas Extras", value: "minutos_extras", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Responsável", value: "aprovador", sortable: false },
        { text: "", value: "acoes", sortable: false },
      ],
      modalAprovar: {
        show: false,
        loading: false,
        item: null,
        observacoes: "",
      },
      modalReprovar: {
        show: false,
        loading: false,
        item: null,
        motivo: "",
      },
      modalHistorico: {
        show: false,
        loading: false,
        user: null,
        dados: { data: [], current_page: 1, last_page: 1 },
      },
    };
  },
  computed: {
    pendenciasTotal() {
      return this.pendencias
        ? this.pendencias.total || (this.pendencias.data ? this.pendencias.data.length : 0)
        : 0;
    },
  },
  async created() {
    this.$loaderService.open("Carregando dados...");
    try {
      await Promise.all([this.carregarPendencias(1), this.carregarSaldos(1)]);
    } catch (e) {
      /* eslint-disable-next-line no-console */
      console.error(e);
    } finally {
      this.$loaderService.close();
    }
  },
  methods: {
    async carregarPendencias(page = 1) {
      this.loadingPendencias = true;
      try {
        const resp = await this.$services.bancoHorasService.getAdminPendencias({
          page,
          ...this.filtrosPendencias,
        });
        this.pendencias = resp || { data: [], current_page: 1, last_page: 1, total: 0 };
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingPendencias = false;
      }
    },
    async carregarSaldos(page = 1) {
      this.loadingSaldos = true;
      try {
        const resp = await this.$services.bancoHorasService.getAdminSaldos({
          page,
          ...this.filtrosSaldos,
        });
        this.saldos = resp || { data: [], current_page: 1, last_page: 1 };
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingSaldos = false;
      }
    },
    abrirModalAprovar(item) {
      this.modalAprovar.item = item;
      this.modalAprovar.observacoes = "";
      this.modalAprovar.show = true;
    },
    async confirmarAprovacao() {
      if (!this.modalAprovar.item) return;
      this.modalAprovar.loading = true;
      try {
        await this.$services.bancoHorasService.aprovar(this.modalAprovar.item.id, {
          observacoes: this.modalAprovar.observacoes,
        });
        this.$toast.success("Horas extras aprovadas com sucesso!");
        this.modalAprovar.show = false;
        await this.carregarPendencias(this.pendencias.current_page);
        await this.carregarSaldos(this.saldos.current_page);
      } catch (e) {
        this.$toast.danger(e?.response?.data?.message || "Erro ao aprovar horas.");
      } finally {
        this.modalAprovar.loading = false;
      }
    },
    abrirModalReprovar(item) {
      this.modalReprovar.item = item;
      this.modalReprovar.motivo = "";
      this.modalReprovar.show = true;
    },
    async confirmarReprovacao() {
      if (!this.modalReprovar.item) return;
      this.modalReprovar.loading = true;
      try {
        await this.$services.bancoHorasService.reprovar(this.modalReprovar.item.id, {
          motivo: this.modalReprovar.motivo,
        });
        this.$toast.success("Horas extras reprovadas.");
        this.modalReprovar.show = false;
        await this.carregarPendencias(this.pendencias.current_page);
        await this.carregarSaldos(this.saldos.current_page);
      } catch (e) {
        this.$toast.danger(e?.response?.data?.message || "Erro ao reprovar horas.");
      } finally {
        this.modalReprovar.loading = false;
      }
    },
    async verHistoricoColaborador(user) {
      this.modalHistorico.user = user;
      this.modalHistorico.show = true;
      await this.carregarHistoricoModal(user.id, 1);
    },
    async carregarHistoricoModal(userId, page = 1) {
      this.modalHistorico.loading = true;
      try {
        const resp = await this.$services.bancoHorasService.getUsuarioHistorico(userId, { page });
        this.modalHistorico.dados = resp || { data: [], current_page: 1, last_page: 1 };
      } catch (e) {
        console.error(e);
      } finally {
        this.modalHistorico.loading = false;
      }
    },
    verPontosDoDia(userId, data) {
      if (!userId || !data) return;
      this.modalHistorico.show = false;
      this.$router.push({
        name: "colaboradores.pontos",
        query: { colaborador_id: userId, data: data.split("T")[0] },
      });
    },
    formatarMinutos(minutos) {
      if (minutos === null || minutos === undefined) return "00:00";
      const sinal = minutos < 0 ? "-" : "";
      const abs = Math.abs(minutos);
      const h = Math.floor(abs / 60);
      const m = abs % 60;
      return `${sinal}${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    },
    formatarData(dataStr) {
      if (!dataStr) return "-";
      const partes = dataStr.split("T")[0].split("-");
      if (partes.length === 3) {
        return `${partes[2]}/${partes[1]}/${partes[0]}`;
      }
      return dataStr;
    },
    getStatusColor(status) {
      if (status === "aprovado") return "green darken-1";
      if (status === "reprovado") return "red darken-1";
      return "orange darken-2";
    },
    getStatusText(status) {
      if (status === "aprovado") return "Aprovado";
      if (status === "reprovado") return "Reprovado";
      return "Pendente";
    },
  },
};
</script>
